import React, { useContext, useEffect, useMemo, useState } from "react";
import { Spin, Empty, Tooltip } from "antd";
import { TbPokeball } from "react-icons/tb";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../../config/AuthContext";
import { GetJutgeUsuariIdPoints } from "../../../dao/jutge/GetJutgeUsuariIdPoints";
import { DeleteJutgeUsuariIdPointsId } from "../../../dao/jutge/DeleteJutgeUsuariIdPointsId";

/**
 * Iframe de CAPTURA del javamon (RPG) para el battleground.
 * Muestra las JAVAballs disponibles del jugador con su % de acierto. Al elegir una:
 *   - Se resuelve la captura (éxito = random < punts/200) y se consume la ball (API).
 *   - Se avisa al juego (Unity) con el resultado para que la ANIMACIÓN represente
 *     si el javamon se captura (se queda en la ball) o se escapa.
 * Reemplaza el panel nativo "CAPTURAR A ..." de Unity (opción B: iframe para elegir,
 * Unity mantiene la animación).
 */

interface Ball {
    id: number;        // instance id (shop_user_item) a consumir
    nom: string;       // nombre visible (tipo de la ball)
    punts: number;     // "coste"/chance base sobre 200
    color?: string;    // color de la bola (las JAVAballs NO tienen imagen, se pintan por color)
    descripcio?: string;
    ball?: string;
}

const GameCapture: React.FC = () => {
    const { isLoggedIn, userId } = useContext(AuthContext);
    const [params] = useSearchParams();
    const navigate = useNavigate();

    const javamonId = params.get("javamonId") || "";
    const javamonNom = params.get("javamonNom") || "";
    const javamonImg = params.get("javamonImg") || "";

    const [loading, setLoading] = useState(true);
    const [balls, setBalls] = useState<Ball[]>([]);
    const [throwing, setThrowing] = useState(false);
    const [resultText, setResultText] = useState<string>("");

    // Solo accesible dentro del iframe del juego.
    useEffect(() => {
        if (window.self === window.top) {
            navigate("/home", { replace: true });
        }
    }, [navigate]);

    useEffect(() => {
        if (!isLoggedIn || !userId) return;
        (async () => {
            setLoading(true);
            try {
                const data = await GetJutgeUsuariIdPoints(userId + "");
                const list: Ball[] = (Array.isArray(data) ? data : [])
                    // Una JAVAball viene marcada con ball === 'JAVAball' (mismo filtro que JavaMon.tsx).
                    .filter((b: any) => b.ball === "JAVAball" || (b.ball === null && b.color !== null))
                    .map((b: any) => ({
                        id: b.id,
                        nom: b.tipo || "JAVAball",
                        punts: b.punts ?? 0,
                        color: b.color,
                        descripcio: b.descripcio,
                        ball: b.ball ?? null,
                    }));
                setBalls(list);
            } catch (e) {
                console.error("Error cargando JAVAballs:", e);
            } finally {
                setLoading(false);
            }
        })();
    }, [isLoggedIn, userId]);

    // En el juego el % de acierto es sobre 150 en lugar de sobre 200.
    // Una bola de 20 puntos es 20 sobre 150 = (20 / 150) * 100%
    const chancePct = (b: Ball) => Math.max(0, Math.min(100, Math.round(((b.punts ?? 0) / 150) * 100)));

    const notifyGame = (type: string, extra: any = {}) => {
        window.parent?.postMessage({ type, ...extra }, "*");
    };

    async function handleThrow(b: Ball) {
        if (throwing) return;
        setThrowing(true);
        setResultText(`Lanzando ${b.nom}...`);

        // En el juego: éxito = tirada (0..150) <= punts, o roll < (punts / 150)
        const chance = Math.max(0, Math.min(1, (b.punts ?? 0) / 150));
        const rollRatio = Math.random();
        const rollValue = Number((rollRatio * 150).toFixed(2));
        const success = rollRatio < chance;

        // Avisar a Unity YA para que empiece la animación de lanzamiento con el resultado real.
        notifyGame("CAPTURE_THROW", { ballName: b.nom, success });

        // Consumir la ball y registrar la captura si procede.
        try {
            const parsedId = javamonId && !isNaN(Number(javamonId)) && Number(javamonId) > 0 ? Number(javamonId) : null;
            const captured = {
                name: success ? (javamonNom || "unknown") : "",
                targetJavamonName: javamonNom || "unknown",
                javamonId: parsedId,
                ballName: b.nom,
                ballPoints: b.punts,
                captured: success,
                origen: "GAME",
                roll: rollValue,
                maxRoll: 150,
                rarity: "normal",
                image: javamonImg || "",
                tier: "dynamic",
                capturedAt: new Date().toISOString(),
            };
            await DeleteJutgeUsuariIdPointsId(userId + "", b.id + "", captured);
        } catch (e) {
            console.error("Error registrando captura:", e);
        }

        setBalls((prev) => prev.filter((x) => x.id !== b.id));
        setResultText(success ? "¡Has capturado al JAVAmon!" : "El JAVAmon se ha escapado...");

        // Dar tiempo a la animación de Unity y luego cerrar + notificar fin.
        setTimeout(() => {
            notifyGame("CAPTURE_DONE", { success });
        }, success ? 2200 : 2600);
    }

    const title = `¡Captura a ${javamonNom || "este JAVAmon"}!`;

    const content = useMemo(() => {
        if (loading) {
            return (
                <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                    <Spin size="large" />
                </div>
            );
        }
        if (throwing) {
            return (
                <div style={{ textAlign: "center", padding: 30 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 16, fontSize: 16, color: "#f5e9d6" }}>{resultText}</div>
                </div>
            );
        }
        if (balls.length === 0) {
            return (
                <div style={{ textAlign: "center", marginTop: 20 }}>
                    <Empty
                        description={<span style={{ color: "#d8c7ad" }}>No tienes JAVAballs. El JAVAmon escapará.</span>}
                    />
                    <button
                        onClick={() => {
                            // Sin balls no se puede capturar: resolvemos como ESCAPE y cerramos.
                            setThrowing(true);
                            setResultText("El JAVAmon se ha escapado...");
                            notifyGame("CAPTURE_THROW", { ballName: "", success: false });
                            setTimeout(() => notifyGame("CAPTURE_DONE", { success: false }), 2200);
                        }}
                        style={{
                            marginTop: 18,
                            background: "linear-gradient(90deg, #7a5c38 0%, #a07a4a 100%)",
                            border: "2px solid #c79a5c",
                            color: "#fff4e0",
                            fontWeight: 700,
                            fontSize: 15,
                            padding: "10px 26px",
                            borderRadius: 10,
                            cursor: "pointer",
                        }}
                    >
                        Continuar
                    </button>
                </div>
            );
        }
        return (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 12 }}>
                {balls.map((b) => (
                    <Tooltip key={b.id} title={`Acierto: ${chancePct(b)}%`}>
                        <div
                            onClick={() => handleThrow(b)}
                            style={{
                                background: "radial-gradient(circle at 50% 30%, #6b5035 0%, #4a3824 100%)",
                                border: "2px solid #7a5c38",
                                borderRadius: 12,
                                padding: 12,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 6,
                                cursor: "pointer",
                                transition: "transform 0.1s, border-color 0.1s",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.06)"; e.currentTarget.style.borderColor = "#f0c060"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.borderColor = "#7a5c38"; }}
                        >
                            <div style={{ fontWeight: 700, color: "#f5e9d6", fontSize: 13 }}>{b.nom}</div>
                            <TbPokeball style={{ width: 52, height: 52, color: b.color || "#ccc", filter: "drop-shadow(0 2px 3px rgba(0,0,0,0.5))" }} />
                            <div style={{ color: "#52c41a", fontWeight: 700, fontSize: 13 }}>Acierto: {chancePct(b)}%</div>
                        </div>
                    </Tooltip>
                ))}
            </div>
        );
    }, [loading, throwing, resultText, balls]);

    return (
        <div
            style={{
                width: "100%",
                height: "100%",
                minHeight: "100%",
                boxSizing: "border-box",
                padding: 20,
                background: "linear-gradient(180deg, #3b2a1a 0%, #2a1d12 100%)",
                color: "#f5e9d6",
                fontFamily: "'Segoe UI', sans-serif",
                overflowY: "auto",
            }}
        >
            <div style={{ textAlign: "center", marginBottom: 16 }}>
                <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: 1, textShadow: "0 2px 0 rgba(0,0,0,0.4)" }}>
                    🎯 {title}
                </span>
            </div>
            {content}
        </div>
    );
};

export default GameCapture;
